import type { GameState, Move } from '../game/types';
import { isInCheck } from '../game/rules';

export interface ThinkingTimePolicy {
  minTimeMs: number;
  maxTimeMs: number;
  jitterRatio: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function allocateThinkingTime(
  state: GameState,
  moves: readonly Move[],
  policy: ThinkingTimePolicy,
  random: () => number = Math.random,
): number {
  const minTimeMs = Math.max(1, Math.min(policy.minTimeMs, policy.maxTimeMs));
  const maxTimeMs = Math.max(minTimeMs, policy.maxTimeMs);
  const pieceCount = state.board.reduce((count, piece) => count + (piece ? 1 : 0), 0);
  const opening = state.fullmove <= 12 && pieceCount >= 26;
  const phaseFactor = opening ? 0 : pieceCount <= 14 ? 0.45 : 0.25;
  const mobilityFactor = Math.min(moves.length / 60, 1) * 0.15;
  const captureCount = moves.reduce((count, move) => count + (move.captured ? 1 : 0), 0);
  const captureFactor = Math.min(captureCount / 6, 1) * 0.2;
  const checkFactor = isInCheck(state, state.turn) ? 0.25 : 0;
  const positionalFactor = (mobilityFactor + captureFactor) * (opening ? 0.8 : 1);
  const complexity = clamp(
    phaseFactor + positionalFactor + checkFactor,
    0,
    1,
  );
  const baseTimeMs = minTimeMs + (maxTimeMs - minTimeMs) * complexity;
  const sampledRandom = random();
  const randomValue = Number.isFinite(sampledRandom) ? clamp(sampledRandom, 0, 1) : 0.5;
  const jitterRatio = clamp(policy.jitterRatio, 0, 0.5);
  const jitter = 1 - jitterRatio + randomValue * jitterRatio * 2;

  return Math.round(clamp(baseTimeMs * jitter, minTimeMs, maxTimeMs));
}
